import Map from 'ol/Map.js';
import OSM from 'ol/source/OSM.js';
import VectorSource from 'ol/source/Vector.js';
import GPX from 'ol/format/GPX.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import Overlay from 'ol/Overlay.js';
import View from 'ol/View.js';
import {boundingExtent} from 'ol/extent.js';
import {fromLonLat, transform, get as getProj, transformExtent} from 'ol/proj.js';
import {Tile as TileLayer, Vector as VectorLayer} from 'ol/layer.js';
import {Style, Fill, Stroke, Circle, Icon} from 'ol/style.js';
import Feature from 'ol/Feature.js';
import {Point} from 'ol/geom.js';
import crelt from 'crelt';
import FullScreen from 'ol/control/FullScreen.js';
import {defaults as defaultControls} from 'ol/control/defaults.js';
import {bbox as bboxStrategy} from 'ol/loadingstrategy.js';

import {mapStyle, genericFeatureStyle} from './map-styles.js';
import {isDarkMode, onColorSchemeChange} from '../color-scheme.js';


const cssStyle = new CSSStyleSheet();
cssStyle.replaceSync(`
  .map-target {
    display: block;
    box-shadow: 0 0 6px var(--box-shadow-color);
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius);
    overflow: hidden;
    width: 100%;
    height: 45vh;
    min-width: 300px;
    min-height: 300px;
  }

  .consent-dialog {
    box-sizing: border-box;
    display: flex;
    align-content: center;
    flex-direction: column;
    gap: 0.5rem;
    padding: var(--default-padding-x);
    width: 100%;
    height: 100%;
    background-color: rgba(var(--info-rgb), 0.1);
  }

  h3,
  h3 + p {
    color: light-dark(var(--info-text-emphasis), rgba(var(--info-rgb), 0.8));
  }

  .consent-dialog .text-muted {
    color: var(--secondary-color);
  }

  .consent-dialog button {
    border-radius: var(--border-radius);
    border: 1px solid var(--info);
    background-color: var(--info-bg-subtle);
    color: var(--info-text-emphasis);
    padding: 0.5rem 0.5rem;
    cursor: pointer;
    transition: background-color 250ms ease;
  }

  .consent-dialog button:hover {
    background-color: var(--info);
  }

  .ol-attribution {
    bottom: 0;
  }

  site-marker {
    box-sizing: border-box;
    display: block;
    width: 44px;
    height: 44px;
  }

  site-marker a {
    box-sizing: border-box;
    border: 1px solid #ddd;
    border-radius: 50%;
    overflow: hidden;
    height: 100%;
    width 100%;
    display: block;
    padding: 4px;
    background: var(--body-bg);
  }

  site-marker a img {
    box-sizing: border-box;
    width: 100%;
    height: 100%;
  }

  site-marker a.inactive {
    border-color: var(--danger);
    background-color: var(--danger-bg-subtle);
    box-shadow: 0 0 3px 3px var(--danger);
  }

  site-marker a.inactive img {
    opacity: 0.8;
  }

  @keyframes pulse {
    to {
      box-shadow: 0 0 0 50px rgba(118, 184, 40, 0);
    }
  }

  site-marker a.active {
    border-color: #0a0;
    box-shadow: 0 0 0 0 rgba(118, 184, 40, 0.7);
    cursor: pointer;
    background-color: rgba(118, 184, 40, 0.7);
  }
  site-marker a.active.activity-0 {
    animation: pulse 3s infinite cubic-bezier(.55,.06,.68,.19);
  }
  site-marker a.active.activity-1 {
    animation: pulse 2s infinite cubic-bezier(.55,.06,.68,.19);
  }
  site-marker a.active.activity-2 {
    animation: pulse 1.5s infinite cubic-bezier(.55,.06,.68,.19);
  }
  site-marker a.active.activity-3 {
    animation: pulse 1s infinite cubic-bezier(.55,.06,.68,.19);
  }
  site-marker a.active.activity-4 {
    animation: pulse 0.7s infinite cubic-bezier(.55,.06,.68,.19);
  }

  .popover-content {
    min-width: 180px;
  }

  .ol-box {
      box-sizing: border-box;
      border-radius: 2px;
      border: 2px solid #00f;
  }
  .ol-mouse-position {
      top: 8px;
      right: 8px;
      position: absolute;
  }
  .ol-scale-line {
      background: rgba(0,60,136,.3);
      border-radius: 4px;
      bottom: 8px;
      left: 8px;
      padding: 2px;
      position: absolute;
  }
  .ol-scale-line-inner {
      border: 1px solid #eee;
      border-top: none;
      color: #eee;
      font-size: 10px;
      text-align: center;
      margin: 1px;
      will-change: contents,width;
  }
  .ol-overlay-container {
      will-change: left,right,top,bottom;
  }
  .ol-unsupported {
      display: none;
  }
  .ol-unselectable,
  .ol-viewport {
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
      -webkit-tap-highlight-color: transparent;
  }
  .ol-selectable {
      -webkit-touch-callout: default;
      -webkit-user-select: text;
      -moz-user-select: text;
      -ms-user-select: text;
      user-select: text;
  }
  .ol-grabbing {
      cursor: -webkit-grabbing;
      cursor: -moz-grabbing;
      cursor: grabbing;
  }
  .ol-grab {
      cursor: move;
      cursor: -webkit-grab;
      cursor: -moz-grab;
      cursor: grab;
  }
  .ol-control {
      position: absolute;
      background-color: rgba(var(--body-color-rgb),.4);
      border-radius: 4px;
      padding: 2px;
  }
  .ol-control:hover {
      background-color: rgba(var(--body-color-rgb),.6);
  }
  .ol-zoom {
      top: 0.5em;
      left: 0.5em;
  }
  .ol-rotate {
      top: 0.5em;
      right: 0.5em;
      transition: opacity 0.25s linear,visibility 0s linear;
  }
  .ol-rotate.ol-hidden {
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.25s linear,visibility 0s linear 0.25s;
  }
  .ol-zoom-extent {
      top: 4.643em;
      left: 0.5em;
  }
  .ol-full-screen {
      right: 0.5em;
      top: 0.5em;
  }
  @media print {
      .ol-control {
          display: none;
      }
  }
  .ol-control button {
      display: block;
      margin: 1px;
      padding: 0;
      color: var(--body-color);
      font-size: 1.14em;
      font-weight: 700;
      text-decoration: none;
      text-align: center;
      height: 1.375em;
      width: 1.375em;
      line-height: 0.4em;
      background-color: rgba(var(--secondary-rgb),.5);
      border: none;
      border-radius: 2px;
  }
  .ol-control button::-moz-focus-inner {
      border: none;
      padding: 0;
  }
  .ol-zoom-extent button {
      line-height: 1.4em;
  }
  .ol-compass {
      display: block;
      font-weight: 400;
      font-size: 1.2em;
      will-change: transform;
  }
  .ol-touch .ol-control button {
      font-size: 1.5em;
  }
  .ol-touch .ol-zoom-extent {
      top: 5.5em;
  }
  .ol-control button:focus,
  .ol-control button:hover {
      text-decoration: none;
      background-color: rgba(var(--secondary-rgb),.7);
  }
  .ol-zoom .ol-zoom-in {
      border-radius: 2px 2px 0 0;
  }
  .ol-zoom .ol-zoom-out {
      border-radius: 0 0 2px 2px;
  }
  .ol-attribution {
      text-align: right;
      bottom: 0.5em;
      right: 0.5em;
      max-width: calc(100% - 1.3em);
  }
  .ol-attribution ul {
      margin: 0;
      padding: 0 0.5em;
      font-size: 0.7rem;
      line-height: 1.375em;
      color: #000;
      text-shadow: 0 0 2px #fff;
  }
  .ol-attribution li {
      display: inline;
      list-style: none;
      line-height: inherit;
  }
  .ol-attribution li:not(:last-child):after {
      content: " ";
  }
  .ol-attribution img {
      max-height: 2em;
      max-width: inherit;
      vertical-align: middle;
  }
  .ol-attribution button,
  .ol-attribution ul {
      display: inline-block;
  }
  .ol-attribution.ol-collapsed ul {
      display: none;
  }
  .ol-attribution:not(.ol-collapsed) {
      background: rgba(var(--body-color-rgb),.8);
  }
  .ol-attribution.ol-uncollapsible {
      bottom: 0;
      right: 0;
      border-radius: 4px 0 0;
      height: 1.1em;
      line-height: 1em;
  }
  .ol-attribution.ol-uncollapsible img {
      margin-top: -.2em;
      max-height: 1.6em;
  }
  .ol-attribution.ol-uncollapsible button {
      display: none;
  }
  .ol-zoomslider {
      top: 4.5em;
      left: 0.5em;
      height: 200px;
  }
  .ol-zoomslider button {
      position: relative;
      height: 10px;
  }
  .ol-touch .ol-zoomslider {
      top: 5.5em;
  }
  .ol-overviewmap {
      left: 0.5em;
      bottom: 0.5em;
  }
  .ol-overviewmap.ol-uncollapsible {
      bottom: 0;
      left: 0;
      border-radius: 0 4px 0 0;
  }
  .ol-overviewmap .ol-overviewmap-map,
  .ol-overviewmap button {
      display: inline-block;
  }
  .ol-overviewmap .ol-overviewmap-map {
      border: 1px solid #7b98bc;
      height: 150px;
      margin: 2px;
      width: 150px;
  }
  .ol-overviewmap:not(.ol-collapsed) button {
      bottom: 1px;
      left: 2px;
      position: absolute;
  }
  .ol-overviewmap.ol-collapsed .ol-overviewmap-map,
  .ol-overviewmap.ol-uncollapsible button {
      display: none;
  }
  .ol-overviewmap:not(.ol-collapsed) {
      background: rgba(var(--body-color-rgb),.8);
  }
  .ol-overviewmap-box {
      border: 2px dotted rgba(var(--secondary-rgb),.7);
  }
  .ol-overviewmap .ol-overviewmap-box:hover {
      cursor: move;
  }

`);

class MapExtension extends HTMLElement {
  constructor() {
    super();
  }

  fireConnectedEvent(component) {
    const evt = new CustomEvent(`${component}:connected`, {
      bubbles: true,
      cancelable: false
    });
    this.dispatchEvent(evt);
  }
}

// `<map-box-with-coords-pickers eventbus>` exists in templates only as a
// generic host element marked with the `eventbus` attribute; the closest()
// lookup in MapBox / SitePicker / CoordsPicker uses that attribute to find
// the bus. It is not a registered custom element and needs no JS class.

// Wrapper for Lon and Lat input fields. Updates Picker position on change.
export class CoordsPicker extends MapExtension {
  constructor() {
    super();
  }

  connectedCallback() {
    this.eventBus = this.closest('[eventbus]') || this;
    this._onPick   = this.setCoords.bind(this);
    this._onChange = this.dispatchCoordsChanged.bind(this);
    this.eventBus.addEventListener('mapbox:pick', this._onPick);
    this.addEventListener('change', this._onChange);
    this.dispatchEvent(new CustomEvent('picker:setup', {bubbles: true}));
  }

  disconnectedCallback() {
    if (this.eventBus) {
      this.eventBus.removeEventListener('mapbox:pick', this._onPick);
      this.removeEventListener('change', this._onChange);
      this.dispatchEvent(new CustomEvent('picker:teardown', {bubbles: true}));
    }

    this.eventBus = undefined;
  }

  setCoords(event) {
    // if (event.detail.id == this.getAttribute('id')) {
      this.coords = event.detail.coords;
    // }
  }

  get lonInput() {
    return this.querySelector('input.lon');
  }

  get latInput() {
    return this.querySelector('input.lat');
  }

  get mapBox() {
    return this.querySelector('map-box');
  }

  get lon() {
    if (this.lonInput.value === '') {
      return parseFloat(this.lonInput.dataset.default);
    } else {
      return this.lonInput.value;
    }
  }

  set lon(value) {
    const step = (parseFloat(this.lonInput.step) || 0.00005);
    this.lonInput.value = Math.round(value / step) * step;
    this.lonInput.dispatchEvent(new Event('change', {bubbles: true}));
  }

  get lat() {
    if (this.latInput.value === '') {
      return parseFloat(this.latInput.dataset.default);
    } else {
      return this.latInput.value;
    }
  }

  set lat(value) {
    const step = (parseFloat(this.latInput.step) || 0.00005);
    this.latInput.value = Math.round(value / step) * step;
    this.latInput.dispatchEvent(new Event('change', {bubbles: true}));
  }

  get coords() {
    return [this.lon, this.lat];
  }

  set coords(c) {
    this.lon = c[0];
    this.lat = c[1];
  }

  get pos() {
    return fromLonLat(this.coords);
  }

  dispatchCoordsChanged(event) {
    const evt = new CustomEvent('picker:coords-changed', {
      bubbles: true,
      cancelable: false,
      detail: {
        coords: this.coords
      }
    });
    this.dispatchEvent(evt);
  }
}


class BBoxedElement extends HTMLElement {
  constructor() {
    super();
  }

  get boundsEast() {
    if (this.hasAttribute('bounds-east')) {
      return parseFloat(this.getAttribute('bounds-east'));
    }
  }

  get boundsWest() {
    if (this.hasAttribute('bounds-west')) {
      return parseFloat(this.getAttribute('bounds-west'));
    }
  }

  get boundsNorth() {
    if (this.hasAttribute('bounds-north')) {
      return parseFloat(this.getAttribute('bounds-north'));
    }
  }

  get boundsSouth() {
    if (this.hasAttribute('bounds-south')) {
      return parseFloat(this.getAttribute('bounds-south'));
    }
  }

  get bbox() {
    if (this.boundsNorth && this.boundsEast && this.boundsSouth && this.boundsWest) {
      return [[this.boundsWest, this.boundsNorth], [this.boundsEast, this.boundsSouth]];
    }
  }
}

export class SiteMarker extends MapExtension {
  constructor() {
    super();
  }

  connectedCallback() {
    this.fireConnectedEvent('site-marker');
  }

  toOverlay() {
    return new Overlay({
      position:    this.pos,
      positioning: 'center-center',
      element:     this,
      stopEvent:   false
    });
  }

  get coords() {
    return [this.lon, this.lat];
  }

  get pos() {
    return fromLonLat(this.coords);
  }

  get lon() {
    if (this.hasAttribute('lon')) {
      return parseFloat(this.getAttribute('lon'));
    }
  }

  get lat() {
    if (this.hasAttribute('lat')) {
      return parseFloat(this.getAttribute('lat'));
    }
  }

}

export class SitePicker extends SiteMarker {
  constructor() {
    super();
  }

  connectedCallback() {
    this.eventBus = this.closest('[eventbus]');
    this._onSetPos = this.setPos.bind(this);
    this.eventBus.addEventListener('picker:coords-changed', this._onSetPos);
    this.eventBus.addEventListener('mapbox:pick',           this._onSetPos);

    this.feature = new Feature({
      geometry: new Point(this.pos),
    });
    this.fireConnectedEvent('site-picker');
  }

  disconnectedCallback() {
    if (this.eventBus) {
      this.eventBus.removeEventListener('picker:coords-changed', this._onSetPos);
      this.eventBus.removeEventListener('mapbox:pick',           this._onSetPos);
    }
    this.eventBus = undefined;
  }

  setPos(evt) {
    this.setAttribute('lon', evt.detail.coords[0]);
    this.setAttribute('lat', evt.detail.coords[1]);
    this.feature.getGeometry().setCoordinates(this.pos);
  }

  get img() {
    return this.getAttribute('img');
  }

  toLayer() {
    const style = new Style({
      image: new Icon({
        src:    this.img,
        anchor: [0.5, 1],
      })
    });
    const source = new VectorSource();
    source.addFeature(this.feature);

    return new VectorLayer({
      source: source,
      style:  style,
    });
  }
}


const osmConsentTpl = crelt('template', {});
osmConsentTpl.innerHTML = `
<div class="consent-dialog">
  <h3>Externe Inhalte von Open Street Map</h3>
  <p>Wenn Sie Inhalte von openstreetmap.org (OSM) zulassen, wird hier eine Karte angezeigt.</p>
  <button type="button">Inhalte von OSM zulassen</button>
  <p class="text-muted">Ihre Zustimmung wird auf Ihrem Endgerät gespeichert und nicht an amphis.de übertragen.</p>
</div>
`;

export class MapBox extends BBoxedElement {
  constructor() {
    super();
    this.setupComplete = false;
  }

  connectedCallback() {
    if (!this.setupComplete && this.isConnected) {
      this.shadow = this.attachShadow({mode: 'open'});
      this.target = crelt('div', {class: 'map-target'});
      this.shadow.appendChild(this.target);
      this.shadow.adoptedStyleSheets.push(cssStyle);

      if (localStorage != undefined && localStorage.getItem("osmIsOK") == 'YES') {
        this.connectedWithConsent();
      } else {
        this.connectedWithoutConsent();
      }
    } else {
      // console.log(this.setupComplete, this.isConnected)
    }
  }


  disconnectedCallback() {
    if (this.eventBus) {
      this.eventBus.removeEventListener('site-marker:connected', this._onMarkerConnected);
      this.eventBus.removeEventListener('site-picker:connected', this._onPickerConnected);
      this.eventBus.removeEventListener('geo-input:connected',   this._onExtensionConnected);
      this.eventBus.removeEventListener('map-layer:connected',   this._onExtensionConnected);
    }
    if (this.map) {
      this.map.un('click', this._onClick);
    }
    this.setupComplete = false;
  }

  connectedWithConsent() {
    this.eventBus = this.closest('[eventbus]') || this;
    this._onMarkerConnected    = this.handleMarkerConnected.bind(this);
    this._onPickerConnected    = this.handlePickerConnected.bind(this);
    this._onExtensionConnected = this.handleExtensionConnected.bind(this);
    this.eventBus.addEventListener('site-marker:connected', this._onMarkerConnected);
    this.eventBus.addEventListener('site-picker:connected', this._onPickerConnected);
    this.eventBus.addEventListener('geo-input:connected',   this._onExtensionConnected);
    this.eventBus.addEventListener('map-layer:connected',   this._onExtensionConnected);

    this.setup();
  }

  connectedWithoutConsent() {
    const dialog = document.importNode(osmConsentTpl.content, true);
    const consentBtn = dialog.querySelector('button');
    this.target.appendChild(dialog);
    consentBtn.addEventListener('click', e => {
      localStorage.setItem('osmIsOK', 'YES');
      this.target.innerHTML = '';
      this.setup();
    });
  }

  preRenderDarkTheme(evt) {
    if (evt.context) {
      const context = evt.context;
      context.filter = 'invert(100%) hue-rotate(180deg) grayscale(70%) ';
      context.globalCompositeOperation = 'source-over';
    }
  }

  postRenderDarkTheme(evt) {
    if (evt.context) {
      const context = evt.context;
      context.filter = 'none';
    }
  }

  handleColorSchemeChange(event) {
    if (event.matches) {
      this.tileLayer.on('prerender', this.preRenderDarkTheme);
      this.tileLayer.on('postrender', this.postRenderDarkTheme);
    } else {
      this.tileLayer.un('prerender', this.preRenderDarkTheme);
      this.tileLayer.un('postrender', this.postRenderDarkTheme);
    }
    this.vectorSource.refresh();
  }

  setup() {
    this.vectorSource = new VectorSource();
    this.vectorLayer  = new VectorLayer({
      source: this.vectorSource,
      style:  mapStyle['normal']
    });

    this.tileLayer = new TileLayer({ source: new OSM() });

    this._onColorSchemeChange = this.handleColorSchemeChange.bind(this);
    onColorSchemeChange(this._onColorSchemeChange);
    this.handleColorSchemeChange({matches: isDarkMode()});

    this.map = new Map({
      controls: defaultControls().extend([new FullScreen()]),
      layers: [ this.tileLayer, this.vectorLayer ],
      target: this.target,
      view:   new View({
        center: fromLonLat([9, 51]),
        zoom: 6
      })
    });

    if (this.gpxURL) {
      this.gpxLayer = new VectorLayer({
        source: new VectorSource({
          url:    this.gpxURL,
          format: new GPX()
        }),
        style: (feature, _resolution) => mapStyle[feature.getGeometry().getType()]
      });
      this.map.addLayer(this.gpxLayer);
    }

    if (this.geoJsonURL) {
      const geoJsonURL = this.geoJsonURL;

      const geoJsonSource = new VectorSource({
        loader: (extent, resolution, projection, success, failure) => {
          const extentString = transformExtent(extent, projection, getProj('EPSG:4326')).join(',');
          const url = `${geoJsonURL}?bbox=${extentString}`;
          const xhr = new XMLHttpRequest();
          xhr.open('GET', url);
          const onError = function() {
            geoJsonSource.removeLoadedExtent(extent);
            failure();
          }
          xhr.onerror = onError;
          xhr.onload = function() {
            if (xhr.status == 200) {
              const features = geoJsonSource.getFormat().readFeatures(JSON.parse(xhr.responseText));
              geoJsonSource.addFeatures(features);
              success(features);
            } else {
              onError();
            }
          }
          xhr.send();
        },
        strategy: bboxStrategy,
        format: new GeoJSON({dataProjection: getProj('EPSG:4326'), featureProjection: getProj('EPSG:3857')})
      });

      // Heatmap support and feature-styling specific to a domain (species
      // colours, quantity-driven weights) live in <map-vector-layer>'s
      // declarative API; the legacy `geojson-url` attribute on <map-box>
      // intentionally uses only the generic geometry-type styler.
      const vectorLayer = new VectorLayer({
        source: geoJsonSource,
        minZoom: 17,
        maxZoom: 30,
        style: (feature, _resolution) => genericFeatureStyle(feature),
      });
      this.map.addLayer(vectorLayer);
    }

    if (this.centerCoords) {
      this.centerOn(this.centerCoords, 13);
    } else if (this.bbox) {
      this.fitToBBox(this.bbox);
    }

    // const geonameMarkers = document.querySelectorAll('geo-name-marker');
    // if (geonameMarkers.length > 0) {
    //   geonameMarkers.forEach(boxElement => {
    //     const geometry = new Polygon.fromExtent(boxElement.bbox);
    //     const feature  = new Feature({
    //       geometry: geometry.transform('EPSG:4326', this.map.getView().getProjection())
    //     });
    //     this.vectorSource.addFeature(feature);
    //   });
    // }

    this._onClick = this.handleClickEvent.bind(this);
    this.map.on('click', this._onClick);

    // Hover handling: emit feature:hover / feature:unhover events on the
    // eventbus. Consumers (e.g. <map-feature-info>) listen and render
    // whatever they want. MapBox keeps the on-map style swap (highlight
    // the hovered feature) since that's a map-internal concern.
    let currentFeature;
    const updateHover = (pixel, target) => {
      const feature = target.closest('.ol-control')
        ? undefined
        : this.map.forEachFeatureAtPixel(pixel, (f) => f);
      if (currentFeature && feature !== currentFeature) {
        // Clear the per-feature override so the layer's own style function
        // takes over again (works regardless of which styler the layer uses).
        currentFeature.setStyle(undefined);
      }
      if (feature && feature !== currentFeature) {
        feature.setStyle(mapStyle[`${feature.getGeometry().getType()}Hover`]);
        this.eventBus.dispatchEvent(new CustomEvent('feature:hover', {
          bubbles: false,
          detail: {feature, pixel},
        }));
      } else if (!feature && currentFeature) {
        this.eventBus.dispatchEvent(new CustomEvent('feature:unhover', {
          bubbles: false,
        }));
      }
      currentFeature = feature;
    };

    this.map.on('pointermove', (evt) => {
      if (evt.dragging) {
        if (currentFeature) {
          this.eventBus.dispatchEvent(new CustomEvent('feature:unhover', {bubbles: false}));
          currentFeature = undefined;
        }
        return;
      }
      updateHover(evt.pixel, evt.originalEvent.target);
    });

    this.map.getTargetElement().addEventListener('pointerleave', _ => {
      if (currentFeature) {
        this.eventBus.dispatchEvent(new CustomEvent('feature:unhover', {bubbles: false}));
        currentFeature = undefined;
      }
    });

    this.setupComplete = true;
  }

  handleMarkerConnected(e) {
    this.map.addOverlay(e.target.toOverlay());
  }

  handlePickerConnected(e) {
    this.picker = e.target;
    this.map.addLayer(this.picker.toLayer());
  }

  handleExtensionConnected(e) {
    const extension = e.target;
    extension.connectToMap(this.map);
  }

  handleClickEvent(clickEvent) {
    this.dispatchPickEvent(transform(clickEvent.coordinate, 'EPSG:3857', 'EPSG:4326'));
  }

  dispatchPickEvent(coords) {
    const evt = new CustomEvent('mapbox:pick', {
      bubbles: true,
      cancelable: false,
      detail: {
        coords: coords
      }
    });
    this.dispatchEvent(evt);
  }

  setPickerPos(val) {
    this.picker.setAttribute('lon', val[0]);
    this.picker.setAttribute('lat', val[1]);
    // console.log(this.picker.coords)
    this.dispatchPickEvent(this.picker.coords);
    this.picker.feature.getGeometry().setCoordinates(this.picker.pos);

    this.centerOn(this.picker.coords, this.map.getView().getZoom());
  }

  get gpxURL() {
    return this.getAttribute('gpx-url');
  }

  get geoJsonURL() {
    return this.getAttribute('geojson-url');
  }

  get centerLon() {
    if (this.hasAttribute('center-lon')) {
      return parseFloat(this.getAttribute('center-lon'));
    }
  }

  get centerLat() {
    if (this.hasAttribute('center-lat')) {
      return parseFloat(this.getAttribute('center-lat'));
    }
  }

  get centerCoords() {
    if (this.centerLon && this.centerLat) {
      return [this.centerLon, this.centerLat];
    }
  }

  addFeature(feature) {
    this.vectorSource.addFeature(feature);
  }

  fitToBBox(bbox) {
    let ext = boundingExtent(bbox);
    let box = transformExtent(ext, getProj('EPSG:4326'), getProj('EPSG:3857'));
    this.map.getView().fit(box, {size: this.map.getSize(), padding: [20, 20, 20, 20]});
  }

  centerOn(coords, zoomlevel) {
    this.map.setView(new View({
      center: transform(coords, 'EPSG:4326', 'EPSG:3857'),
      zoom:   zoomlevel
    }));
  }
}

